import { Typography, Stack, TableContainer, Box } from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import NestedCard from 'src/components/reports/report-cards/nested-card';
import EngagementNestedCard from 'src/components/reports/report-cards/engagement-nested-card';
import { CommonFunction } from 'src/utils/commonFunctions/common-function.component';
import { useState, useRef, useEffect } from 'react';
import { useToaster } from 'src/utils/toaster/toasterContext';
import HttpServices from 'src/services/httpService';
import { reportModuleURL } from 'src/services/urlService';
import { useTranslation } from 'react-i18next';
import Charts from './report-cards/charts';

const CustomReportBodyComponent = ({ customReportData, fetchCustomReports }) => {
  const { t: translate } = useTranslation();
  const { showToast } = useToaster();
  const scrollContainerRef = useRef(null);

  const [reportCards, setReportCards] = useState(
    customReportData?.report_cards
      ?.filter((report) => report.report_card_details.is_display)
      ?.sort((a, b) => b.report_card_details.card_order - a.report_card_details.card_order) || []
  );

  useEffect(() => {
    if (customReportData) {
      setReportCards(
        customReportData?.report_cards
          ?.filter((report) => report.report_card_details.is_display)
          ?.sort((a, b) => b.report_card_details.card_order - a.report_card_details.card_order) || []
      );
    }
  }, [customReportData]);

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const reorderedCards = Array.from(reportCards);
    const [movedCard] = reorderedCards.splice(result.source.index, 1);
    reorderedCards.splice(result.destination.index, 0, movedCard);

    const updatedCards = reorderedCards.map((card, index) => ({
      ...card,
      report_card_details: {
        ...card.report_card_details,
        card_order: reorderedCards.length - index,
      },
    }));

    setReportCards(updatedCards);

    const payload = {
      custom_report_id: customReportData.custom_report_id,
      report_cards: updatedCards.map((card) => ({
        report_card_id: card.report_card_details.id,
        card_order: card.report_card_details.card_order,
      })),
    };

    const postProps = {
      url: reportModuleURL.updateCardOrder,
      body: payload,
      successCallback: (data, message) => {
        showToast(message, 's');
      },
      failureCallback: (message) => {
        showToast(message, 'e');
        setReportCards(
          customReportData?.report_cards
            ?.filter((report) => report.report_card_details.is_display)
            ?.sort((a, b) => b.report_card_details.card_order - a.report_card_details.card_order) || []
        );
      },
    };
    HttpServices.Post(postProps);
  };

const renderComponent = (data, index) => (
  <Draggable
    key={data.report_card_details.id}
    draggableId={String(data.report_card_details.id)}
    index={index}
  >
    {(provided) => (
      <Box
        ref={provided.innerRef}
        {...provided.draggableProps}
        sx={{
          mb: 2,
          px: 1,
          py: 1,
          backgroundColor: '#fafafa',
          borderRadius: '4px',
          position: 'relative',
          '&:hover .drag-icon': { color: 'grey.700' },
          '&:hover': { border: '1px solid #e0e0e0' },
        }}
      >
        <Stack spacing={2}>
          {/* Header Section */}
          <Stack direction="row" spacing={1} alignItems="center">
            <Box
              {...provided.dragHandleProps}
              sx={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'move', // Shows 4-arrow move cursor
                color: 'grey.500',
                '&:hover': { color: 'grey.700' },
              }}
              className="drag-icon"
            >
              <DragIndicatorIcon />
            </Box>

            {CommonFunction.InitialReportAccountBadgeWithName(data)}
            <Typography variant="h6">
              {data.profile_name} : {data.report_card_details.card_name}
            </Typography>
          </Stack>

          {/* Conditional Rendering for Card Types */}
          {data.report_card_details.card_type === 'nested_card' && (
            <NestedCard
              connectedProfileId={data.connected_profile_id}
              startDate={customReportData.start_date}
              endDate={customReportData.end_date}
              viewBy={customReportData.view_by}
              audienceSummary={data.report_card_details}
              serviceName={data.service_name}
              reportId={customReportData.custom_report_id}
              isCustomReport={true}
              fetchCustomReports={fetchCustomReports}
            />
          )}

          {data.report_card_details.card_type === 'nested_card_and_table' && (
            <EngagementNestedCard
              connectedProfileId={data.connected_profile_id}
              startDate={customReportData.start_date}
              endDate={customReportData.end_date}
              viewBy={customReportData.view_by}
              engagementSummary={data.report_card_details}
              serviceName={data.service_name}
              reportId={customReportData.custom_report_id}
              isCustomReport={true}
              fetchCustomReports={fetchCustomReports}
            />
          )}

          {data.report_card_details.card_type === 'chart' && (
            <Charts
              connectedProfileId={data.connected_profile_id}
              startDate={customReportData.start_date}
              endDate={customReportData.end_date}
              viewBy={customReportData.view_by}
              columnCharts={data.report_card_details}
              serviceName={data.service_name}
              reportId={customReportData.custom_report_id}
              isCustomReport={true}
              fetchCustomReports={fetchCustomReports}
            />
          )}
        </Stack>
      </Box>
    )}
  </Draggable>
);


  return (
    <Box sx={{ height: '100%', position: 'relative' }}>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="reportCards">
          {(provided) => (
            <TableContainer
              ref={(el) => {
                provided.innerRef(el);
                scrollContainerRef.current = el;
              }}
              {...provided.droppableProps}
              sx={{
                mt: 1,
                minHeight: 'calc(100vh - 200px)',
                maxHeight: 'calc(100vh - 200px)',
                overflowY: 'auto',
                overflowX: 'hidden',
                position: 'relative',
              }}
            >
              {reportCards.length > 0 ? (
                reportCards.map((data, index) => renderComponent(data, index))
              ) : (
                <Typography>{translate('custom_report.no_report_card')}</Typography>
              )}
              {provided.placeholder}
            </TableContainer>
          )}
        </Droppable>
      </DragDropContext>
    </Box>
  );
};

export default CustomReportBodyComponent;
